import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import { useTranslation } from '@/hooks/useTranslation'

const formSchema = z.object({
	retentionDays: z.number().min(1).max(365),
	anonymizeData: z.boolean(),
	autoDelete: z.boolean(),
})

const DataSettings = () => {
	const { t } = useTranslation()
	const { toast } = useToast()
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			retentionDays: 90,
			anonymizeData: false,
			autoDelete: true,
		},
	})

	const onSubmit = (values: z.infer<typeof formSchema>) => {
		toast({
			title: t('dataSettings.settingsUpdated'),
			description: t('dataSettings.dataSettingsSavedSuccessfully'),
		})
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="retentionDays"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('dataSettings.dataRetentionPeriod')}</FormLabel>
							<FormControl>
								<Input
									type="number"
									{...field}
									onChange={(e) => field.onChange(Number(e.target.value))}
								/>
							</FormControl>
							<FormDescription>
								{t('dataSettings.numberOfDaysToKeepResponseData')}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="anonymizeData"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									{t('dataSettings.anonymizeResponseData')}
								</FormLabel>
								<FormDescription>
									{t('dataSettings.removePersonallyIdentifiableInformation')}
								</FormDescription>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="autoDelete"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									{t('dataSettings.autoDeleteOldData')}
								</FormLabel>
								<FormDescription>
									{t('dataSettings.automaticallyDeleteDataOlderThanRetentionPeriod')}
								</FormDescription>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				<Button type="submit">{t('dataSettings.saveChanges')}</Button>
			</form>
		</Form>
	)
}

export default DataSettings
